import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { useMeta, useSnapshot } from "@/lib/AppContext";
import { TYPE_ICON } from "@/lib/api";
import { LogoutButton } from "@/components/AuthGate";
import logoWhite from "@/assets/sng-logo.png";

const Icon = ({ name, className }) => {
  const C = Icons[
    (name || "circle").split("-").map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join("")
  ] || Icons.Circle;
  return <C className={className} />;
};

export default function Sidebar() {
  const { meta } = useMeta();
  const { snapshot } = useSnapshot();
  const { sid } = useParams();
  const loc = useLocation();
  if (!meta) return null;
  const base = `/s/${sid}`;

  return (
    <aside data-testid="sidebar" className="w-64 shrink-0 h-screen sticky top-0 bg-[#1a1a17] text-stone-300 flex flex-col border-r border-black/40">
      <div className="px-5 pt-5 pb-3 border-b border-white/10">
        <img src={logoWhite} alt="S&G" data-testid="sidebar-logo" className="h-7 w-auto" />
      </div>
      <Link to="/" className="px-5 py-4 border-b border-white/10 block hover:bg-white/5" data-testid="all-snapshots-link">
        <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500 flex items-center gap-1">
          <Icons.ChevronLeft className="w-3 h-3" /> All snapshots
        </div>
        <div className="font-display text-lg text-amber-50 leading-tight mt-1 truncate" title={snapshot?.name}>
          {snapshot?.name || "…"}
        </div>
      </Link>

      <nav className="flex-1 overflow-y-auto py-3">
        <Link
          to={base}
          data-testid="nav-dashboard"
          className={`flex items-center gap-2.5 px-5 py-2 text-sm transition-colors ${loc.pathname === base ? "text-amber-200 bg-white/5" : "hover:text-amber-100 hover:bg-white/5"}`}
        >
          <Icons.LayoutDashboard className="w-4 h-4" /> Dashboard
        </Link>

        {Object.entries(meta.categories).map(([cat, types]) => (
          <div key={cat} className="mt-4">
            <div className="px-5 mb-1 text-[10px] uppercase tracking-[0.2em] text-stone-600">{cat}</div>
            {types.map((t) => {
              const active = loc.pathname.startsWith(`${base}/catalog/${t}`);
              const count = snapshot?.counts?.[t];
              return (
                <Link
                  key={t}
                  to={`${base}/catalog/${t}`}
                  data-testid={`nav-${t}`}
                  className={`flex items-center justify-between px-5 py-1.5 text-sm transition-colors ${active ? "text-amber-200 bg-white/5 border-l-2 border-amber-500" : "text-stone-400 hover:text-amber-100 hover:bg-white/5 border-l-2 border-transparent"}`}
                >
                  <span className="flex items-center gap-2.5">
                    <Icon name={TYPE_ICON[t]} className="w-4 h-4" />
                    {meta.type_labels[t]}
                  </span>
                  {count != null && <span className="text-[11px] text-stone-500 mono">{count}</span>}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <Link to={`${base}/export`} data-testid="nav-export" className="px-5 py-3 border-t border-white/10 text-sm flex items-center gap-2 hover:text-amber-100">
        <Icons.Download className="w-4 h-4" /> Export &amp; Safety
      </Link>
      <Link to="/account" data-testid="sidebar-account-link" className="px-5 py-3 border-t border-white/10 text-sm text-stone-400 hover:text-amber-100">Account</Link>
      <LogoutButton className="px-5 py-3 border-t border-white/10 text-left text-stone-400 hover:text-amber-100" />
    </aside>
  );
}
